import * as THREE from 'three';
import { cloneExtrudeEndTarget, cloneSelectionItemRefData, resolveOwningFeatureResultMesh, resolveTopologySelectionPick } from '../../../utils/feature';
import { isSketchGroup, SketchPointObject } from '../../../mesh/sketch';
import { getDatumPlaneIdFromObject, isDatumPlaneObject } from '../../../reference';
import type { Viewer } from '../../../viewer';
// binding barrel包含反向依赖本解析器的ExtrudeReferenceFeatureBinding，使用具体类避免初始化循环。
import { ModelTopologyFeatureBinding } from '../../binding/ModelTopologyFeatureBinding';

/**
 * 将宿主选择转换为拉伸终止目标的稳定引用。
 * 该对象不持有场景或选择对象；每次调用只返回可持久化纯数据。
 */
export class ExtrudeEndTargetResolver {
  /**
   * 复用正式绑定重定位当前目标，生成输入行和场景交互身份，不缓存历史显示名。
   * @param target 持久化目标。
   * @param viewer 当前查看器。
   * @param bodyNames 设计树结果体名称映射。
   * @param materializeTopologyProxy 仅实际选择或悬停时挂载代理，并立即交给查看器交互生命周期。
   * @returns 当前显示投影；丢失引用仍保留可删除的明确身份。
   */
  public resolveDisplay(target: ExtrudeEndTarget, viewer: Viewer, bodyNames: ReadonlyMap<string, string> = new Map(), materializeTopologyProxy = false): ExtrudeEndTargetDisplay {
    if (target.kind === 'body' || target.kind === 'model-topology') {
      const bodyObjectId = target.kind === 'body' ? target.bodyObjectId : target.selection.bodyObjectId;
      const resolved = new ModelTopologyFeatureBinding(ModelTopologyFeatureBinding.createPayload({ bodyObjectId,
        items: target.kind === 'model-topology' ? [target.selection] : [] })).resolve({ viewer });
      const owner = resolved?.kind === 'model-topology' ? resolved.object : null;
      const item = resolved?.kind === 'model-topology' ? resolved.items[0] : undefined;
      const index = item?.runtimeTopologyHint?.topologyIndex;
      const key = target.kind === 'body' ? bodyObjectId : target.selection.persistentRef || target.selection.subEntityId || bodyObjectId;
      let object = owner;
      const topologyOwner = owner ? resolveOwningFeatureResultMesh(owner) : null;
      // 稳定body别名可指向新的运行时修订，名称映射优先查询当前权威结果体身份。
      let label = bodyNames.get(bodyObjectId) || (topologyOwner ? bodyNames.get(topologyOwner.objectId) : null)
        || (owner?.name !== bodyObjectId && owner?.name !== topologyOwner?.objectId ? owner?.name : null) || '实体';
      if (target.kind === 'model-topology') {
        const snapshot = topologyOwner?.getTopologySnapshot();
        const entries = item?.kind === 'face' ? snapshot?.faces : snapshot?.vertices;
        const current = entries?.find((entry) => entry.index === index);
        // 通用绑定在失配时保留旧提示；显示端必须验证稳定身份，不能误高亮同索引的新面。
        const valid = current && (current.ref === item?.persistentRef
          || (Boolean(item?.subEntityId) && current.subEntityId === item?.subEntityId));
        object = valid && typeof index === 'number'
          ? (materializeTopologyProxy ? (item?.kind === 'face' ? topologyOwner?.attachFeatureFaceByIndex(index) : topologyOwner?.attachFeaturePointByIndex(index)) ?? null : owner)
          : null;
        // 代理name含运行时创建计数，重建会变化；输入行使用当前权威拓扑序号和设计树实体名。
        label = `${item?.kind === 'vertex' ? '顶点' : '面'}${typeof index === 'number' ? index + 1 : ''} · ${label}`;
      }
      return { key, label: object ? label : `${label}（引用已失效：${key}）`,
        sceneObjectId: target.kind === 'model-topology' && !materializeTopologyProxy ? null : object?.uuid ?? null };
    }
    const object = this.resolveReferenceObject(target, viewer.scene);
    const key = target.kind === 'sketch-point' ? `${target.sketchId}:${target.pointId}` : `datum-plane:${target.defaultPlaneKind ?? target.planeId}`;
    const label = target.kind === 'sketch-point' ? `${object?.name || '草图点'} · ${object?.parent?.name || '草图'}` : object?.name || `${target.defaultPlaneKind === 'front' ? '前视' : target.defaultPlaneKind === 'top' ? '上视' : target.defaultPlaneKind === 'right' ? '右视' : ''}基准面`;
    return { key, label: object ? label : `${label}（引用已失效：${key}）`, sceneObjectId: object?.uuid ?? null };
  }

  /** 按稳定草图点或基准面身份查找当前对象，显示和几何求值共用同一解析规则。 */
  private resolveReferenceObject(target: ExtrudeEndTarget, scene: THREE.Scene | null): THREE.Object3D | null {
    let matched: THREE.Object3D | null = null;
    scene?.traverse((object) => {
      if (target.kind === 'sketch-point' && isSketchGroup(object) && object.persistentId === target.sketchId) {
        const point = object.getObjectByPersistentId(target.pointId);
        if (point instanceof SketchPointObject) matched = point;
      }
      if (target.kind === 'datum-plane' && isDatumPlaneObject(object)
        && (target.defaultPlaneKind ? Reflect.get(object, 'datumPlaneDefaultKind') === target.defaultPlaneKind : getDatumPlaneIdFromObject(object) === target.planeId)) matched = object;
    });
    return matched;
  }
  /**
   * 从当前选择对象解析目标。
   * @param object 当前权威选择对象。
   * @param condition 终止条件。
   * @returns 满足条件的目标；不符合选择类型时返回 null。
   */
  public resolve(object: THREE.Object3D | null | undefined, condition: ExtrudeEndCondition, topologyPick?: ResolvedTopologyPick | null): ExtrudeEndTarget | null {
    if (!object) return null;
    if (condition === 'up_to_vertex' && object instanceof SketchPointObject) {
      let parent = object.parent;
      while (parent && !isSketchGroup(parent)) parent = parent.parent;
      if (!parent || !isSketchGroup(parent)) return null;
      return { kind: 'sketch-point', sketchId: parent.persistentId, pointId: object.persistentId };
    }
    const planeId = getDatumPlaneIdFromObject(object);
    if (planeId !== null && ['up_to_surface', 'offset_from_surface'].includes(condition)) {
      const defaultKind = Reflect.get(object, 'datumPlaneDefaultKind');
      return { kind: 'datum-plane', planeId,
        defaultPlaneKind: ['front', 'top', 'right'].includes(defaultKind) ? defaultKind : undefined };
    }
    if (condition === 'up_to_body') {
      const owner = resolveOwningFeatureResultMesh(object);
      if (!owner) {
        // 导入模型的选择代理可直接给出权威 Worker 实体身份，无需假设可见 mesh 是特征结果类。
        return topologyPick?.bodyObjectId
          ? { kind: 'body', bodyObjectId: topologyPick.bodyObjectId, owningFeatureId: topologyPick.owningFeatureId }
          : null;
      }
      const metadata = owner.getFeatureMetadata();
      return {
        kind: 'body', bodyObjectId: owner.objectId,
        owningFeatureId: typeof metadata.featureId === 'string' ? metadata.featureId : undefined,
      };
    }
    const pick = topologyPick === undefined ? resolveTopologySelectionPick(object) : topologyPick;
    const expectedKind = condition === 'up_to_vertex' ? 'vertex' : 'face';
    if (!pick || pick.kind !== expectedKind) return null;
    return { kind: 'model-topology', selection: cloneSelectionItemRefData(pick) };
  }

  /**
   * 执行或重建前重新解析正式草图点、基准面，禁止拿旧快照兜底悬空引用。
   * @param target 稳定引用。
   * @param scene 当前场景。
   * @returns 已补齐当前几何的纯数据目标。
   */
  public resolveRuntime(target: ExtrudeEndTarget, scene: THREE.Scene | null): ExtrudeEndTarget {
    if (target.kind === 'model-topology' || target.kind === 'body') return cloneExtrudeEndTarget(target)!;
    const source = this.resolveReferenceObject(target, scene);
    if (!source) throw new Error('拉伸终止引用已丢失或被删除。');
    source.updateWorldMatrix(true, false);
    const origin = source.getWorldPosition(new THREE.Vector3());
    if (target.kind === 'sketch-point') return { ...target, runtimePoint: [origin.x, origin.y, origin.z] };
    const normal = new THREE.Vector3(0, 0, 1).transformDirection(source.matrixWorld).normalize();
    return { ...target, runtimePlane: { origin: [origin.x, origin.y, origin.z], normal: [normal.x, normal.y, normal.z] } };
  }
}
